import type { NavItem } from './navConfig';

const RAIL_ICON_PX = 34;
const RAIL_PROFILE_ICON_PX = 37;
const RAIL_DISCOVER_ICON_PX = 38;

// 8px grid. Tight padding keeps a hit target above 44px on the smaller icons.
const RAIL_PAD_PX = { min: 8, max: 12 } as const;
const RAIL_GAP_PX = { min: 8, max: 24 } as const;
const RAIL_NAV_PAD_PX = { min: 12, max: 24 } as const;

interface RailSpacing {
    pad: number
    gap: number
    navPad: number
}

const RAIL_TIGHT: RailSpacing = { pad: RAIL_PAD_PX.min, gap: RAIL_GAP_PX.min, navPad: RAIL_NAV_PAD_PX.min };
const RAIL_ROOMY: RailSpacing = { pad: RAIL_PAD_PX.max, gap: RAIL_GAP_PX.max, navPad: RAIL_NAV_PAD_PX.max };

export const railIconPx = (id: NavItem['id']) => {
    if (id === 'discover') return RAIL_DISCOVER_ICON_PX;
    if (id === 'profile') return RAIL_PROFILE_ICON_PX;
    return RAIL_ICON_PX;
};

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

// Floor, so rounding cannot push the stack past the measured box and clip an icon.
const lerpDown = (min: number, max: number, t: number) => Math.floor((min + (max - min) * t) * 100) / 100;

/** Icons, hit padding, gaps, and the nav's own vertical padding. */
const stackHeight = (items: readonly NavItem[], spacing: RailSpacing) => {
    const icons = items.reduce((sum, item) => sum + railIconPx(item.id), 0);
    const gaps = Math.max(0, items.length - 1) * spacing.gap;
    return icons + items.length * spacing.pad * 2 + gaps + spacing.navPad * 2;
};

export interface RailLayout {
    items: NavItem[]
    gap: number
    pad: number
    navPad: number
}

/** Reported by the rail before it has a box, and when every icon has been moved to the menu. */
export const EMPTY_RAIL_VISIBLE: ReadonlySet<NavItem['id']> = new Set();

export const railLayoutHeight = ({ items, gap, pad, navPad }: RailLayout) => stackHeight(items, { gap, pad, navPad });

/**
 * Fit a prefix of `allItems` into `boxHeight`. Items are ordered most important first, so a short
 * rail keeps Discover and drops from the end. Whatever this omits is the header menu's job.
 * A non-positive height means the rail has not been measured; every item stays, and nothing is
 * reported as on screen.
 */
export const layoutRail = (allItems: readonly NavItem[], boxHeight: number): RailLayout => {
    const roomy = { items: [...allItems], gap: RAIL_ROOMY.gap, pad: RAIL_ROOMY.pad, navPad: RAIL_ROOMY.navPad };
    if (boxHeight <= 0) return roomy;

    const items = [...allItems];
    while (items.length > 0 && stackHeight(items, RAIL_TIGHT) > boxHeight) {
        items.pop();
    }

    if (items.length === 0) {
        return { items, gap: 0, pad: 0, navPad: 0 };
    }

    const minHeight = stackHeight(items, RAIL_TIGHT);
    const maxHeight = stackHeight(items, RAIL_ROOMY);
    const span = maxHeight - minHeight;
    const t = span <= 0 ? 1 : clamp01((boxHeight - minHeight) / span);

    return {
        items,
        gap: lerpDown(RAIL_GAP_PX.min, RAIL_GAP_PX.max, t),
        pad: lerpDown(RAIL_PAD_PX.min, RAIL_PAD_PX.max, t),
        navPad: lerpDown(RAIL_NAV_PAD_PX.min, RAIL_NAV_PAD_PX.max, t),
    };
};

/** Ids actually painted in the rail. Empty until the rail has a measured height. */
export const railVisibleIds = (items: readonly NavItem[], boxHeight: number): ReadonlySet<NavItem['id']> => {
    if (boxHeight <= 0) return EMPTY_RAIL_VISIBLE;
    return new Set(layoutRail(items, boxHeight).items.map((item) => item.id));
};
